'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
}

const FAQSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const faqs: FAQ[] = [
    {
      question: 'What is EPC and what services does it include?',
      answer: 'EPC stands for Engineering, Procurement, and Construction. Our comprehensive EPC services cover complete project lifecycle from initial design and engineering, procurement of equipment and materials, to construction, installation, testing, and commissioning. We handle electrical and mechanical systems for infrastructure, industrial, healthcare, and commercial projects.'
    },
    {
      question: 'What certifications and qualifications does New Capital hold?',
      answer: 'We are certified by EPRA (Energy and Petroleum Regulatory Authority), NCA (National Construction Authority), and ICT (Information and Communications Technology). We maintain ISO 9001:2015 quality management certification and are authorized distributors for Schneider Electric, Toshiba, and Fuji Electric. Our team includes over 200 certified engineers with international training.'
    },
    {
      question: 'Which countries do you operate in?',
      answer: 'We currently operate across East Africa, with established offices and completed projects in Rwanda, Kenya, and Burundi. Our regional presence allows us to serve clients efficiently while maintaining consistent quality standards across all locations.'
    },
    {
      question: 'How long does a typical EPC project take?',
      answer: 'Project timelines vary based on scope and complexity. Small to medium projects typically take 3-6 months, while large infrastructure projects may require 12-18 months. We pride ourselves on delivering projects 20% faster than industry average through efficient processes and experienced project management.'
    },
    {
      question: 'Do you provide maintenance and after-sales support?',
      answer: 'Yes, we offer comprehensive after-sales support including 24/7 emergency response, preventive maintenance programs, genuine spare parts supply, and technical training. Our maintenance contracts ensure optimal system performance and longevity of your electrical and mechanical installations.'
    },
    {
      question: 'What makes New Capital different from other EPC contractors?',
      answer: 'Our competitive advantages include direct manufacturer partnerships ensuring genuine products at competitive prices, zero-incident safety record over 5 million man-hours, ISO-certified quality management, 200+ expert engineers, and proven success in mission-critical projects like Amahoro Stadium and Bugesera Airport.'
    },
    {
      question: 'How do you ensure project quality and safety?',
      answer: 'We maintain rigorous quality control through ISO 9001:2015 certified processes, regular inspections, and compliance with international standards. Our zero-incident safety culture includes comprehensive training, strict protocols, and continuous monitoring. All equipment is sourced directly from Tier-1 manufacturers with full warranties.'
    },
    {
      question: 'Can you handle projects with tight deadlines?',
      answer: 'Yes, we specialize in fast-track project delivery. Our efficient processes, experienced team, and strong supplier relationships enable us to meet urgent timelines without compromising quality. We have successfully delivered emergency projects within 1-2 months when required.'
    },
    {
      question: 'What is your payment structure for EPC projects?',
      answer: 'Payment terms are customized based on project scope and timeline. Typically, we follow a milestone-based payment structure: initial deposit upon contract signing, progress payments tied to completion stages, and final payment upon successful commissioning and handover. We offer flexible terms for government and institutional clients.'
    },
    {
      question: 'Do you provide project financing or payment plans?',
      answer: 'We work with financial institutions to facilitate project financing for qualified clients. For large projects, we can arrange payment plans and collaborate with development banks. Contact our team to discuss financing options tailored to your project requirements.'
    }
  ];

  const toggleFAQ = (index: number) => {
    if (!isHydrated) return;
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <section className="py-20 bg-card">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-headline text-4xl md:text-5xl font-bold text-primary mb-4">
              Frequently Asked Questions
            </h2>
            <p className="font-body text-xl text-muted-foreground">
              Common questions about our EPC services and capabilities
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-surface rounded-lg shadow-card overflow-hidden transition-all duration-250"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full p-6 flex items-center justify-between hover:bg-muted transition-colors duration-250 text-left"
                >
                  <span className="font-headline text-lg font-semibold text-primary pr-4">
                    {faq.question}
                  </span>
                  <Icon
                    name="ChevronDownIcon"
                    size={24}
                    variant="outline"
                    className={`text-primary transition-transform duration-250 flex-shrink-0 ${
                      isHydrated && expandedIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isHydrated && expandedIndex === index && (
                  <div className="px-6 pb-6 border-t border-border">
                    <p className="font-body text-muted-foreground pt-4 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-br from-primary to-secondary rounded-lg p-8 text-center text-white">
            <h3 className="font-headline text-2xl font-bold mb-3">Still Have Questions?</h3>
            <p className="font-body mb-6 opacity-90">
              Our team is ready to provide detailed answers and discuss your specific project requirements
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#contact"
                className="bg-white hover:bg-white/90 text-primary font-cta font-bold px-8 py-3 rounded-lg transition-all duration-250 inline-flex items-center justify-center gap-2"
              >
                <Icon name="EnvelopeIcon" size={20} variant="solid" />
                Contact Us
              </a>
              <a
                href="tel:+250788123456"
                className="bg-accent hover:bg-accent/90 text-white font-cta font-bold px-8 py-3 rounded-lg transition-all duration-250 inline-flex items-center justify-center gap-2"
              >
                <Icon name="PhoneIcon" size={20} variant="solid" />
                Call Now
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;